/**
 * migrateMd.js — Convert legacy markdown content to Tiptap JSON
 *
 * Existing resources store content as plain text with:
 *   **bold**    → Strong mark
 *   ==highlight== → Highlight mark
 *   \n          → Paragraph break
 *
 * This utility detects whether content is already Tiptap JSON
 * (object or JSON string starting with {) and skips migration.
 */

/**
 * Convert a markdown string to Tiptap-compatible JSON document
 */
export function mdToTiptap(md) {
    if (!md) {
        return { type: 'doc', content: [{ type: 'paragraph' }] };
    }

    // Already Tiptap JSON (object)
    if (typeof md === 'object' && md.type === 'doc') return md;

    // Already Tiptap JSON (serialised string)
    if (typeof md === 'string' && md.trimStart().startsWith('{')) {
        try {
            const parsed = JSON.parse(md);
            if (parsed.type === 'doc') return parsed;
        } catch { /* not JSON, treat as markdown */ }
    }

    // Parse markdown → paragraphs
    const lines = md.split('\n');
    const paragraphs = [];

    for (const line of lines) {
        if (line.trim() === '') {
            paragraphs.push({ type: 'paragraph' });
            continue;
        }

        const nodes = parseLine(line);
        paragraphs.push({ type: 'paragraph', content: nodes });
    }

    return { type: 'doc', content: paragraphs.length ? paragraphs : [{ type: 'paragraph' }] };
}

/**
 * Parse a single line into Tiptap text nodes with marks
 */
function parseLine(text) {
    const nodes = [];
    // Combined regex for ==highlight== and **bold**
    const regex = /(==(?:.*?)==|\*\*(?:.*?)\*\*)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        // Plain text before match
        if (match.index > lastIndex) {
            nodes.push({ type: 'text', text: text.slice(lastIndex, match.index) });
        }

        const raw = match[0];
        if (raw.startsWith('==') && raw.endsWith('==')) {
            const inner = raw.slice(2, -2);
            if (inner) {
                nodes.push({
                    type: 'text',
                    text: inner,
                    marks: [{ type: 'highlight' }],
                });
            }
        } else if (raw.startsWith('**') && raw.endsWith('**')) {
            const inner = raw.slice(2, -2);
            if (inner) {
                nodes.push({
                    type: 'text',
                    text: inner,
                    marks: [{ type: 'bold' }],
                });
            }
        }

        lastIndex = regex.lastIndex;
    }

    // Remaining plain text
    if (lastIndex < text.length) {
        nodes.push({ type: 'text', text: text.slice(lastIndex) });
    }

    return nodes.length ? nodes : [{ type: 'text', text: ' ' }];
}

/**
 * Convert Tiptap JSON back to legacy markdown string (for backward compat)
 */
export function tiptapToMd(doc) {
    if (!doc || !doc.content) return '';

    return doc.content.map(node => {
        if (node.type === 'paragraph' && node.content) {
            return node.content.map(n => {
                let text = n.text || '';
                const marks = n.marks || [];
                for (const mark of marks) {
                    if (mark.type === 'bold') text = `**${text}**`;
                    if (mark.type === 'highlight') text = `==${text}==`;
                }
                return text;
            }).join('');
        }
        return '';
    }).join('\n');
}

/**
 * Check if content is already in Tiptap JSON format
 */
export function isTiptapJson(content) {
    if (typeof content === 'object' && content?.type === 'doc') return true;
    if (typeof content === 'string' && content.trimStart().startsWith('{')) {
        try {
            return JSON.parse(content).type === 'doc';
        } catch { return false; }
    }
    return false;
}
